import { useState, type FormEvent } from "react";
import { api } from "../../api/apiClient";

const MIN_LEN = 8;

/** Changing your own password — G-145: a section of `EditProfilePanel`, closed until asked for. */
export function ChangePasswordSection() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNewPwd] = useState("");
  const [confirmNew, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetFields = () => {
    setCurrent("");
    setNewPwd("");
    setConfirm("");
    setError(null);
  };

  const close = () => {
    setOpen(false);
    resetFields();
  };

  const newOk = newPassword.length >= MIN_LEN;
  const matches = newPassword.length > 0 && newPassword === confirmNew;
  const distinct = currentPassword.length > 0 && newPassword !== currentPassword;
  const canSubmit = currentPassword.length > 0 && newOk && matches && distinct && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setSuccess("Password updated. Use the new password the next time you sign in.");
      resetFields();
      setOpen(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="group" className="profile-edit-section" aria-labelledby="profile-edit-password">
      <div className="profile-edit-row">
        <div className="profile-edit-row-text">
          <h3 id="profile-edit-password">Password</h3>
        </div>
        {!open ? (
          <button
            type="button"
            className="button tinted"
            onClick={() => {
              setSuccess(null);
              setOpen(true);
            }}
          >
            Change password
          </button>
        ) : null}
      </div>

      {success ? <div className="alert success">{success}</div> : null}

      {open ? (
        <form className="form-grid" onSubmit={submit}>
          {error ? <div className="alert error full">{error}</div> : null}
          <div className="field full">
            <label htmlFor="cp-current">Current password *</label>
            <input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="cp-new">New password *</label>
            <input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LEN}
              maxLength={128}
              placeholder={`At least ${MIN_LEN} characters`}
              value={newPassword}
              onChange={(e) => setNewPwd(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="cp-confirm">Confirm new password *</label>
            <input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LEN}
              maxLength={128}
              value={confirmNew}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {confirmNew.length > 0 && !matches ? (
              <small className="field-error">The two new passwords don&apos;t match.</small>
            ) : null}
          </div>
          <div className="button-row full">
            <button type="submit" className="button" disabled={!canSubmit}>
              {busy ? "Updating…" : "Update password"}
            </button>
            <button type="button" className="button plain" onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
